import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Grid,
  IconButton,
  TextField,
  Typography,
  Chip,
  Stack,
} from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FilterSelector from '../../../SystemDesign/FilterSelector';
import SearchInput from '../../../SystemDesign/SearchInput';

const ACTIONS = [
  'CREATE',
  'READ',
  'UPDATE',
  'DELETE',
  'BULK_UPDATE',
  'BULK_DELETE',
  'OTHER',
];

const PanelHeader = ({ open, toggle, isFetching, colors, activeCount }) => (
  <Box display="flex" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
    <Stack direction="row" spacing={1} alignItems="center">
      <FilterAltIcon fontSize="small" />
      <Typography variant="h6" sx={{ color: colors.grey[100] }}>Filters</Typography>
      {isFetching && (
        <Typography variant="body2" color="text.secondary">Loading...</Typography>
      )}
      {activeCount > 0 && (
        <Chip size="small" color="primary" variant="outlined" label={`${activeCount} active`} />
      )}
    </Stack>
    <IconButton onClick={toggle} size="small" aria-label="toggle-filters" sx={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}>
      <ExpandMoreIcon />
    </IconButton>
  </Box>
);

const AuditFiltersPanel = ({ initialValues, onApply, onReset, isFetching, colors, searchValue, onSearchChange }) => {
  const [open, setOpen] = useState(true);
  const [form, setForm] = useState({
    dateFrom: '',
    dateTo: '',
    action: 'all', // use 'all' sentinel for FilterSelector
    success: 'all', // 'all' | 'true' | 'false'
  });

  useEffect(() => {
    if (!initialValues) return;
    setForm((prev) => ({
      ...prev,
      dateFrom: initialValues.dateFrom ?? '',
      dateTo: initialValues.dateTo ?? '',
      action: initialValues.action ? initialValues.action : 'all',
      success: typeof initialValues.success === 'boolean' ? String(initialValues.success) : 'all',
    }));
  }, [initialValues]);

  const handleChange = (key) => (valOrEvent) => {
    const value = valOrEvent && valOrEvent.target !== undefined ? valOrEvent.target.value : valOrEvent;
    setForm((f) => ({ ...f, [key]: value }));
  };

  const clearField = (key) => () => setForm((f) => ({ ...f, [key]: (key === 'action' || key === 'success') ? 'all' : '' }));

  const activeChips = useMemo(() => {
    const chips = [];
    if (form.dateFrom) chips.push({ key: 'dateFrom', label: `From: ${form.dateFrom}` });
    if (form.dateTo) chips.push({ key: 'dateTo', label: `To: ${form.dateTo}` });
    if (form.action && form.action !== 'all') chips.push({ key: 'action', label: `Action: ${form.action}` });
    if (form.success !== 'all') chips.push({ key: 'success', label: `Result: ${form.success === 'true' ? 'Success' : 'Failure'}` });
    return chips;
  }, [form]);

  const apply = () => {
    onApply({
      dateFrom: form.dateFrom || '',
      dateTo: form.dateTo || '',
      action: form.action === 'all' ? '' : form.action,
      success: form.success === 'all' ? undefined : form.success === 'true',
    });
  };

  const reset = () => {
    setForm({
      dateFrom: '',
      dateTo: '',
      action: 'all',
      success: 'all',
    });
    onReset?.();
  };

  const activeCount = activeChips.length;

  return (
    <Box sx={{ mb: 2 }}>
      <PanelHeader open={open} toggle={() => setOpen((v) => !v)} isFetching={isFetching} colors={colors} activeCount={activeCount} />

      {activeCount > 0 && (
        <Box sx={{ mb: 1 }}>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {activeChips.map((c) => (
              <Chip key={c.key} label={c.label} onDelete={clearField(c.key)} size="small" />
            ))}
          </Stack>
        </Box>
      )}

      <Collapse in={open} timeout="auto" unmountOnExit>
        <Box sx={{ p: 2, border: `1px solid ${colors.grey[700]}`, borderRadius: 1 }}>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SearchInput
                placeholder=" search by moduls , models .."
                value={searchValue}
                onChange={onSearchChange}
                width="100%"
              />
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="Start date"
                InputLabelProps={{ shrink: true }}
                value={form.dateFrom}
                onChange={handleChange('dateFrom')}
              />
            </Grid>
            <Grid item xs={12} md={6} lg={6}>
              <TextField
                fullWidth
                size="small"
                type="date"
                label="End date"
                InputLabelProps={{ shrink: true }}
                value={form.dateTo}
                onChange={handleChange('dateTo')}
              />
            </Grid>

            <Grid item xs={12} md={6} lg={6}>
              <FilterSelector
                value={form.action}
                onChange={handleChange('action')}
                options={ACTIONS.map(a => ({ value: a, label: a }))}
                defaultLabel="All actions"
                valueKey="value"
                labelKey="label"
                width="100%"
                sx={{ minWidth: { xs: '100%', sm: '160px' } }}
              />
            </Grid>

            <Grid item xs={12} md={6} lg={6}>
              <FilterSelector
                value={form.success}
                onChange={handleChange('success')}
                options={[{ value: 'true', label: 'Success' }, { value: 'false', label: 'Failure' }]}
                defaultLabel="Status"
                valueKey="value"
                labelKey="label"
                width="100%"
                sx={{ minWidth: { xs: '100%', sm: '160px' } }}
              />
            </Grid>

            <Grid item xs={12}>
              <Stack direction="row" spacing={1} justifyContent="flex-end">
                <Button variant="outlined" startIcon={<RestartAltIcon />} onClick={reset}>Reset</Button>
                <Button variant="contained" startIcon={<FilterAltIcon />} onClick={apply}>Apply</Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Collapse>
    </Box>
  );
};

export default AuditFiltersPanel;
